<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from './GaiaButton.vue';
import { gaiaGlyphPaths, gaiaGlyphSize } from '../glyphs';

const props = defineProps({
  /**
   * What is going up — a noun ("version", "current document"). Composes into
   * "Upload version", which becomes the visible label. A caller whose label
   * isn't verb + noun passes the default slot.
   */
  subject: {
    type: String,
    default: '',
  },
  variant: {
    type: String as PropType<'primary' | 'default'>,
    default: 'primary',
  },
  /** `md` for a page header, `sm` for a toolbar or a dialog's commit. */
  size: {
    type: String as PropType<'sm' | 'md'>,
    default: 'md',
  },
  /** Off on a dialog's commit, where the button is the only thing to press. */
  icon: {
    type: Boolean,
    default: true,
  },
  /** Spells out why the action is unavailable; the label needs no tooltip. */
  title: {
    type: String,
    default: '',
  },
});

const text = computed(() => (props.subject ? `Upload ${props.subject}` : 'Upload'));

const glyphSize = computed(() => gaiaGlyphSize[props.size]);
</script>

<template>
  <!-- Starts or commits an upload: bytes leave the machine. It carries the
       arrow rather than the plus for that reason — the one place where the
       glyph is genuinely different rather than the label. A mixed flow that also
       picks files already in the vault can reasonably stay "Add files"; same
       judgement call as Add vs Create.

       NOT the drop target. `GaiaFileUpload` owns click-or-drop and its armed
       drag state; if the control accepts a drop, reach for that instead. This
       is the button that opens such a flow, or commits the modal it opened. -->
  <gaia-button
    :type="props.variant === 'primary' ? 'primary' : undefined"
    :size="props.size === 'md' ? 'medium' : 'small'"
    :title="props.title || undefined"
  >
    <template v-if="props.icon" #icon>
      <svg
        :width="glyphSize"
        :height="glyphSize"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path :d="gaiaGlyphPaths.upload" />
      </svg>
    </template>
    <slot>{{ text }}</slot>
  </gaia-button>
</template>
